import { useState, useEffect, useMemo, useCallback } from 'react';
import useZipcodeValidator from './useZipcodeValidator';
import useGlobalZipcode from './useGlobalZipcode';
import { LocalStorageGeoInfo } from '../utils/constants';
import { removeItemFromLocalStorage } from '../../utils/localStorageUtils';
const useZipcode = ({
  withCity = false,
  ssr = false
} = {}) => {
  if (!ssr && typeof window === 'undefined') {
    return {};
  }
  const {
    globalZip,
    globalZipInfo,
    userInputZip,
    updateGlobalZip
  } = useGlobalZipcode({
    withCity,
    ssr
  });
  const {
    validateZip,
    validateGeolocation
  } = useZipcodeValidator();
  const initialInternalState = {
    zipcode: globalZip,
    zipcodeInfo: globalZipInfo || {},
    zipcodeValid: !!(globalZip && globalZipInfo && globalZipInfo.zip),
    zipcodeError: false
  };
  const [internalState, setZipState] = useState(initialInternalState);
  const {
    zipcode,
    zipcodeInfo,
    zipcodeValid,
    zipcodeError
  } = internalState;
  useEffect(() => {
    setZipState(initialInternalState);
  }, [globalZip, globalZipInfo]);
  const setZip = zipcode => setZipState({
    ...internalState,
    zipcode,
    zipcodeValid: false,
    zipcodeError: false
  });
  const resetZip = () => setZipState(initialInternalState);
  const updateZip = useCallback(async event => {
    const zipcode = event.target.value;
    if (!zipcode || zipcode.match(/^[0-9]*$/)) {
      setZip(zipcode);
      const result = await validateZip(zipcode);
      if (result) {
        setZipState(result);
      }
    }
  }, [setZip, setZipState, validateZip]);
  const requestZip = useCallback(() => {
    navigator.geolocation.getCurrentPosition(async result => {
      const locationInfoByLatLng = await validateGeolocation(result.coords);
      if (locationInfoByLatLng.isValid) {
        setZip(locationInfoByLatLng.zip);
        const result = await validateZip(locationInfoByLatLng.zip);
        if (result) {
          setZipState(result);
        }
      }
    });
  }, [validateGeolocation, validateZip, setZip, setZipState]);
  const submitZip = useCallback(() => {
    if (zipcodeValid) {
      removeItemFromLocalStorage(LocalStorageGeoInfo);
      updateGlobalZip(zipcodeInfo);
    }
  }, [updateGlobalZip, zipcodeValid, zipcodeInfo]);
  const location = useMemo(() => ({
    userInputZip,
    zipcode,
    zipcodeInfo,
    state: zipcodeInfo.stateNonLower,
    city: zipcodeInfo.city,
    county: zipcodeInfo.county,
    subCounty: zipcodeInfo.subCounty,
    isMajorMarket: zipcodeInfo.isMajorMarket,
    zipcodeValid,
    zipcodeError,
    requestZip,
    updateZip,
    submitZip,
    resetZip
  }), [userInputZip, zipcode, zipcodeInfo, zipcodeValid, zipcodeError, requestZip, updateZip, submitZip, resetZip]);
  return location;
};
useZipcode.displayName = 'useZipcode';
export default useZipcode;